import { ColoursObj, LoadingSpinner, MobileAppFrame } from "@eventr/common-storybook";
import { Box, Stack } from "@mui/material";
import { accountRoutes } from "app/account/account.routes";
import { activityFeedRoutes } from "app/activity-feed/activity-feed.routes";
import { AuthLayout } from "app/auth/auth-layout";
import { authRoutes } from "app/auth/auth.routes";
import { legacyAuthRedirects } from "app/auth/legacy-auth-redirects";
import { chatRootRoutes, chatSubRoutes } from "app/chat/chat.routes";
import { clockRoutes } from "app/clocks/clocks.routes";
import { createItineraryRoutes } from "app/create-itinerary/create-itinerary.routes";
import { editRoutes, editSectionRoutes } from "app/edit/edit.routes";
import { legacyEditRedirects } from "app/edit/legacy-edit-redirects";
import { EditMembersAddPage } from "app/edit/members/add/edit-members-add";
import { guestDuplicateRoutes } from "app/guest-duplicate/guest-duplicate.routes";
import { legacyGuestDuplicateRedirects } from "app/guest-duplicate/legacy-guest-duplicate-redirects";
import { homeRoutes } from "app/home/home.routes";
import { itemRoutes } from "app/items/items.routes";
import { itinerariesRoutes } from "app/itineraries/itineraries.routes";
import { linkRoutes } from "app/link/link.routes";
import { paneRoutes } from "app/panes/panes.routes";
import { notificationsCalendarRoutes } from "app/notifications-calendar/notifications-calendar.routes";
import { sectionRoutes } from "app/sections/sections.routes";
import { notesRoutes } from "app/notes/notes.routes";
import { teamRootRoutes, teamSubRoutes } from "app/team/team.routes";
import { termsOfServiceRoutes } from "app/terms-of-service/terms-of-service.routes";
import { EditSectionRoutes } from "feat/editItinerary/withEditSection";
import {
  isWidthCapped,
  NATIVE_ONE_COLUMN_MAX_WIDTH,
  PHONE_MAX_WIDTH,
  TWO_PANE_COLUMN_MAX_WIDTH,
  useScreenLayout,
} from "feat/layout/screen-layout";
import { useTabRailLeft } from "feat/layout/tab-rail-side.store";
import { useThemeContext } from "state/themeState/themeContext";
import { DisabledTabGuard } from "feat/layout/tab-bar/disabledTabGuard";
import {
  NativeTabbarBridge,
  nativeTabbarReady,
  nativeTabbarVisible,
  useTabBarBottomSafeOffset,
} from "feat/layout/tab-bar/nativeTabbarBridge";
import { NavBar } from "feat/layout/tab-bar/navBar";
import { useNavBarHeight } from "feat/layout/tab-bar/navBar.store";
import { NativePaneSide, useNativePaneSide } from "feat/layout/panes/native-pane-side";
import { useSplitStore } from "feat/nativeNavigation/split.store";
import { PaneShell } from "feat/layout/panes/pane-shell";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { useHasVerticalBar } from "feat/nativeNavigation/vertical-bar";
import { TrailingColumnRailAction } from "feat/nativeNavigation/rail-actions";
import { ViewTopInsetProvider } from "state/windowState/view-top-inset";
import { ScrollDebugOverlay } from "feat/nativeNavigation/ScrollDebugOverlay";
import { useHasFeature } from "hooks/useHasFeature";
import { lazy, Suspense, useEffect, useRef, useState, type WheelEvent } from "react";
import { matchPath, Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import { path } from "routes-utils";
import { routes } from "routing";
import { renderRoutes } from "routing/tree";
import { useShallow } from "zustand/react/shallow";
import { NotFoundPage } from "./feat/404/404";
import { hideSplashOnce } from "./feat/appState/splashController";
import { FileCacher } from "./feat/fileCacher/fileCacher";
import { useGuestStore } from "./feat/guest/guest.store";
import { HomeScrollAwayProvider } from "./feat/home/home-scroll-away";
import { HomeRoutes } from "./feat/home/homeRoutes";
import { OAuthRedirect } from "./feat/oauth/pages/oauthRedirect";
import { DebugChatStatus } from "./feat/rocketchat/debugChatStatus";
import { useBoundStore } from "./state/bound.store";
import { useHomeTab } from "./state/itineraryState/homeTab.store";
import { RouteGuardProvider } from "./state/routeGuardProvider";
import { useScrollContext } from "./state/scrollState/scrollContext";
import { useScrollDisabled } from "./state/scrollState/scrollDisabledContext";
import { ScrollContainerContext, scrollRef } from "./state/scrollState/scrollSignals";
import { useEphemeralStore } from "./state/useEphemeralStore";
import {
  useBackgroundImageUrl,
  useBottomInset,
  useKeyboardHeight,
  useKeyboardOpen,
  useTopInset,
  useWindowStore,
} from "./state/windowState/window.store";
import { Capacitor } from "@capacitor/core";
import { isTeamForWeb, isWebPreview } from "./utils";

// Flip to true to show the on-screen native-nav scroll diagnostics overlay.
const SHOW_SCROLL_DEBUG_OVERLAY = false;

const WelcomePage = lazy(() => import("./feat/welcome/WelcomePage"));

export const AppRoutes = () => {
  const { scrollBehaviour } = useScrollContext(
    useShallow((s) => ({
      scrollBehaviour: s.scrollBehavior,
    })),
  );
  // Per-view, so one tab's inner-scroller screen can't disable scrolling in another
  // tab under native navigation (all tabs share one React tree).
  const { disabled: scrollDisabled } = useScrollDisabled();

  const [innerHeight, setInnerHeight] = useState(window.innerHeight);

  useEffect(() => {
    // Every browser build, not just the preview: Team for Web users resize the window, and a
    // stale height leaves the welcome buttons stranded mid-screen. Native keeps its fixed
    // height and handles the keyboard through keyboardHeight instead.
    if (Capacitor.isNativePlatform() && !isIosNativeNavigation()) return;

    // Under iOS native navigation the window also changes with the device: folding or unfolding
    // an iPhone Duo, an iPad's window resizing. The keyboard's resizes still go through
    // keyboardHeight, so the height is only taken with it down, and again once it has gone.
    const keyboardDown = () =>
      !Capacitor.isNativePlatform() || useWindowStore.getState().keyboardState.openState === "didHide";
    // Each native view is its own web view, and a sheet's is shorter than the main window, so the
    // height comes from the view this frame renders in. `window` is always the main web view.
    const view = localScrollRef.current?.ownerDocument.defaultView ?? window;
    const handler = () => {
      if (keyboardDown()) setInnerHeight(view.innerHeight);
    };
    handler();

    view.addEventListener("resize", handler);
    const stopFollowingKeyboard = useWindowStore.subscribe((state, previous) => {
      if (state.keyboardState.openState === "didHide" && previous.keyboardState.openState !== "didHide") handler();
    });
    return () => {
      view.removeEventListener("resize", handler);
      stopFollowingKeyboard();
    };
  }, []);

  const { pathname } = useLocation();

  const { hasToken, orgsReady } = useBoundStore(
    useShallow((s) => {
      const orgs = s.profile?.organisations;
      return {
        hasToken: s.IDtoken !== undefined,
        // HomeRoutes' gates: a loaded profile with an organisation that is not still being made.
        orgsReady: !!orgs && orgs.length > 0 && !(orgs.length === 1 && orgs[0]?.status === "creating"),
      };
    }),
  );

  const hasChatFeature = useHasFeature("Chat");

  const { mapIsFullscreen } = useEphemeralStore(
    useShallow((s) => {
      return {
        mapIsFullscreen: s.mapIsFullscreen,
      };
    }),
  );

  const localScrollRef = useRef<HTMLDivElement>(null);
  const screenLayout = useScreenLayout();
  const { layout } = screenLayout;
  const columnMaxWidth =
    layout === "two-pane" || screenLayout.card || screenLayout.wide ? TWO_PANE_COLUMN_MAX_WIDTH : PHONE_MAX_WIDTH;
  const tabRailLeft = useTabRailLeft();
  const [railContainer, setRailContainer] = useState<HTMLDivElement | null>(null);

  // Scrolls the app from a wheel over the gutters.
  const scrollFromGutter = (e: WheelEvent) => localScrollRef.current?.scrollBy({ top: e.deltaY });

  useEffect(() => {
    // First real view is on screen, safe to lift the splash. The app-ready signal in
    // App.tsx fires far too early under native navigation.
    hideSplashOnce();
    scrollRef.current = localScrollRef.current;

    return () => {
      scrollRef.current = null;
    };
  }, []);

  // Safari Develop-menu labelling for this native view. Every native view's webview loads
  // the same html via loadHTMLString (no distinct URL), so Safari lists them all
  // generically by host/IP. Tag each child webview's own document (not `document`, which
  // under native nav always resolves to the main webview) with its route so the Develop
  // menu shows a real, distinguishable title per view. Browsers show the title in the tab,
  // so web keeps a fixed product name instead of the route.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      if (isTeamForWeb) document.title = "EventR Team for Web";
      return;
    }
    const ownerDoc = localScrollRef.current?.ownerDocument;
    if (ownerDoc) ownerDoc.title = `EventR — ${pathname}`;
  }, [pathname]);

  const homeTab = useHomeTab();
  const expandMap = mapIsFullscreen && pathname === routes.home.path && homeTab === "map";

  // On iOS native nav, the native bars sit outside the web view. Keep the scroll
  // container at the web-view height so the document does not overflow (which would
  // make its scrollView bounce and steal the scroll from the inner container), and
  // inset the content inside the scroll area instead (paddingTop/Bottom below).
  // On Android and web, the existing system insets are used unchanged.
  const topInset = useTopInset();
  const bottomInset = useBottomInset();
  const keyboardOpen = useKeyboardOpen();
  const keyboardHeight = useKeyboardHeight();
  const backgroundImageUrl = useBackgroundImageUrl();
  const tabBarOffset = useTabBarBottomSafeOffset();
  const navBarHeight = useNavBarHeight();

  const nativeNav = isIosNativeNavigation();
  const isGuestMode = useGuestStore.getState().isGuestMode;
  const signedIn = hasToken || isGuestMode;
  const panesReady = isGuestMode || (hasToken && orgsReady);
  const frameHeight = keyboardOpen ? innerHeight - keyboardHeight : innerHeight;
  // The panes end above a bottom tab bar, which already covers the bottom inset.
  const paneHeight = frameHeight - topInset - bottomInset - Math.max(0, navBarHeight - bottomInset);
  const insetTop = "var(--native-navigation-inset-top, env(safe-area-inset-top, 0px))";
  const insetBottom = "var(--native-navigation-inset-bottom, env(safe-area-inset-bottom, 0px))";
  const topSafeArea = nativeNav ? "0px" : `${topInset}px`;
  const bottomSafeArea = nativeNav ? "0px" : bottomInset + "px";

  // Home / Chat / Team have a full-bleed image header that should reach the top of the
  // screen (under the status bar), so these tab roots opt out of the scroll container's
  // top inset. Their native bars are already hidden (see NativeRootController); pushed
  // sub-screens keep the inset because they have a native bar.
  const edgeToEdgeTopPaths: string[] = [routes.home.path, routes.chat.list.path, routes.team.page.path];
  // The team-member sheet carries its own transparent native bar and a full-bleed image
  // header, so it also wants the content edge-to-edge under the status bar (no scroll-
  // container top inset, since that inset is what leaves a white strip above the image).
  // It has a :userId param, so it needs a pattern match rather than path equality.
  // Guided create has the whole screen while it is up, on Home's column or its split's trailing
  // one, from the top of the screen to the bottom, and clears the status bar and home indicator.
  const takesScreen =
    useSplitStore((s) => s.trailingTakesScreen) && (pathname === "/panes/home" || pathname === routes.home.path);
  const edgeToEdgeTop =
    nativeNav &&
    (takesScreen || edgeToEdgeTopPaths.includes(pathname) || matchPath(routes.team.member.path, pathname) !== null);
  // A vertical bar (iPhone Duo held wide) floats over the web view on whichever side the system
  // puts it and insets the safe area there, with the status bar at its top. Every screen's header,
  // list and buttons would sit under it, so each screen's column stands clear of it. Without the
  // bar the side insets are 0 and nothing moves.
  const clearsVerticalBar = nativeNav;
  const hasVerticalBar = useHasVerticalBar();
  // A one-column screen of iOS native navigation — anything not in a split's pane — is capped and
  // centred like the web's phone column, at the trailing pane's measure. The column box's own
  // `--app-width: 100cqw` follows the cap; the root variable below is for what portals outside it.
  const nativePaneSide = useNativePaneSide();
  const nativeCapped = nativeNav && nativePaneSide === null;

  useEffect(() => {
    if (!nativeNav) return;
    document.documentElement.style.setProperty(
      "--app-width",
      nativeCapped ? `min(100vw, ${NATIVE_ONE_COLUMN_MAX_WIDTH}px)` : "100vw",
    );
  }, [nativeNav, nativeCapped]);

  const colourScheme = useThemeContext((s) => s.selectedColourScheme);
  const customThemeId = useThemeContext((s) => s.customThemeId);
  useEffect(() => {
    document.body.style.backgroundColor = ColoursObj.backgrounds.primary;
  }, [colourScheme, customThemeId]);

  const routeElements = (
    <>
      {/* "Already signed in → home" guard for the welcome screen. On iOS native nav
                NativeRootController swaps the whole root to the tab bar the instant the token
                appears, so this react-router redirect must NOT also fire: it becomes a native
                push onto the auth stack that reset() is tearing down, which crashes the
                transition (the post-login white screen). Forcing auth=true on iOS keeps the
                welcome view rendering harmlessly until the native root swap replaces it. Web
                and Android keep the redirect. Same reason as the guard in auth-sign-in.tsx. */}
      <Route element={<GuardedRoute auth={!hasToken || nativeNav} to={routes.home.path} />}>
        <Route
          path="/"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <WelcomePage />
            </Suspense>
          }
        />
      </Route>

      <Route element={<GuardedRoute auth={signedIn} to={routes.auth.signIn.path} />}>
        {/* Home / chat / team roots keep the home-tab layout (itinerary carousel +
                  profile/org gates via HomeRoutes); sub-pages push full-screen with their
                  own header. One Route element keeps HomeRoutes (and ItineraryCarousel)
                  mounted once on web/Android across Home<->Team switches: remounting it
                  re-runs ItineraryCarousel's safe-area zeroing effect, which can leave the
                  real top inset stuck in the shared scroll container's grid row and show as
                  white space above Team's header. iOS native nav mounts each tab as its own
                  webview regardless, so this only matters on single-document platforms. */}
        <Route element={<HomeRoutes />}>{renderRoutes([...homeRoutes, ...chatRootRoutes, ...teamRootRoutes])}</Route>
        {renderRoutes(teamSubRoutes)}

        {renderRoutes(clockRoutes)}
        {renderRoutes(createItineraryRoutes)}

        {renderRoutes(termsOfServiceRoutes)}

        {/* Legacy /tabs/home, /tabs/rocketChat: NativeRootController's native root
                  list and an iOS widget's hardcoded fallback deeplink still target these. */}
        <Route path="/tabs/home" element={<Navigate replace to={routes.home.path} />} />
        <Route path="/tabs/rocketChat" element={<Navigate replace to={routes.chat.list.path} />} />

        {renderRoutes(editRoutes)}
        <Route element={<EditSectionRoutes />}>{renderRoutes(editSectionRoutes)}</Route>
        {legacyEditRedirects()}

        {renderRoutes(itemRoutes)}

        {/* Legacy item deeplinks (notifications, widget, calendar, and the in-app
                  cards not yet repointed) redirect to the canonical /items paths,
                  carrying the id, query string, and location state through unchanged. */}
        <Route path="/item/flight/:_id" element={<ItemRedirect to="/items/flights" />} />
        <Route path="/item/todo/:_id" element={<ItemRedirect to="/items/todos" />} />
        <Route path="/item/activity/:_id" element={<ItemRedirect to="/items/activities" />} />
        <Route path="/item/hotel/:_id" element={<ItemRedirect to="/items/hotels" />} />
        <Route path="/item/hire-car/:_id" element={<ItemRedirect to="/items/hire-cars" />} />
        <Route path="/item/transport/:_id" element={<ItemRedirect to="/items/transport" />} />
        <Route path="/item/parking/:_id" element={<ItemRedirect to="/items/parking" />} />

        {/* Where the deeplink resolver lands a link it cannot open. */}
        {renderRoutes(linkRoutes)}
        {renderRoutes(paneRoutes)}

        {renderRoutes(sectionRoutes)}
        {renderRoutes(notesRoutes)}

        {/* Legacy /itemList deeplinks. Nothing external points at these (no
                  emailed link, OAuth callback, or notification), but a session can
                  still have one in its native back stack when a live-update JS
                  bundle swaps mid-session, so keep the redirect for that case. */}
        <Route path="/itemList/:section_id" element={<SectionRedirect />} />
        <Route path="/itemList/:section_id/:cat_id" element={<SectionRedirect />} />
        {renderRoutes(accountRoutes)}
        {renderRoutes(itinerariesRoutes)}
        {renderRoutes(activityFeedRoutes)}
        {renderRoutes(notificationsCalendarRoutes)}
      </Route>

      {/* Chat sub-routes require a real session (not just guest mode), unlike the
                rest of the authed block above, and the org's Chat entitlement. */}
      <Route
        element={
          <GuardedRoute
            auth={hasToken && hasChatFeature}
            to={hasToken ? routes.account.subscription.path : routes.auth.signIn.path}
          />
        }
      >
        {renderRoutes(chatSubRoutes)}
      </Route>

      {!isWebPreview && (
        <>
          <Route element={<AuthLayout />}>{renderRoutes(authRoutes)}</Route>
          {legacyAuthRedirects()}
        </>
      )}

      <Route key="7" path={`/${path.signUp}`} element={<Navigate to={routes.auth.signUp.path} replace />} />

      <Route key="8" path={routes.oauthCallback.path} element={<OAuthRedirect />} />

      <Route key="add-users" path={routes.setup.team.path} element={<EditMembersAddPage />} />

      {renderRoutes(guestDuplicateRoutes)}
      {legacyGuestDuplicateRedirects()}

      <Route key="9" path="*" element={<NotFoundPage />} />
    </>
  );

  return (
    <ScrollContainerContext.Provider value={localScrollRef}>
      <RouteGuardProvider>
        {/* Gutter, content column, gutter, tab rail, or the tab rail first with Tabs on the Left.
            NavBar portals the rail into its column, which is zero-width while empty. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isWidthCapped
              ? tabRailLeft
                ? `auto 1fr minmax(0, ${columnMaxWidth}px) 1fr`
                : `1fr minmax(0, ${columnMaxWidth}px) 1fr auto`
              : nativeCapped
                ? `1fr minmax(0, ${NATIVE_ONE_COLUMN_MAX_WIDTH}px) 1fr`
                : "minmax(0, 1fr)",
            // Under a vertical bar this is what shows beside the column, so it is the column's own
            // colour there, and the bar does not stand out from the screen.
            bgcolor: hasVerticalBar ? ColoursObj.backgrounds.primary : ColoursObj.backgrounds.secondary,
            // The panes fill the window across a fold.
            ...(layout === "two-pane" && {
              "@media (horizontal-viewport-segments: 2)": {
                gridTemplateColumns: tabRailLeft ? "auto 0 minmax(0, 1fr) 0" : "0 minmax(0, 1fr) 0 auto",
              },
            }),
          }}
        >
          {isWidthCapped && <div onWheel={scrollFromGutter} />}
          <Box
            sx={{
              minWidth: 0,
              // No gutter children to hold the first and last tracks here, unlike the web cap.
              ...(nativeCapped && { gridColumn: 2 }),
              // The containing block for position: fixed descendants while the column is capped.
              ...(isWidthCapped && {
                [`@media (min-width: ${PHONE_MAX_WIDTH + 1}px)`]: { transform: "translateZ(0)" },
              }),
              // Margins, not padding: fixed descendants are laid out in the padding box. The
              // column is their containing block and what `var(--app-width)` measures.
              ...(clearsVerticalBar && {
                // A full-screen map has hidden the bar's tabs and status bar, but the safe area
                // still holds its width, so the map runs under where it was.
                marginLeft: mapIsFullscreen ? 0 : "env(safe-area-inset-left, 0px)",
                marginRight: mapIsFullscreen ? 0 : "env(safe-area-inset-right, 0px)",
                transform: "translateZ(0)",
                containerType: "inline-size",
                "--app-width": "100cqw",
              }),
            }}
          >
            <MobileAppFrame
              bottomSafeArea={bottomSafeArea}
              topSafeArea={topSafeArea}
              height={keyboardOpen ? innerHeight - keyboardHeight + "px" : innerHeight + "px"}
              width="100%"
              passedRef={localScrollRef}
              overscrollBehaviour="contain"
              scrollStyle={{
                scrollBehavior: scrollBehaviour,
                // Axes set separately, never as the `overflow` shorthand: MobileAppFrame's own
                // style sets overflowX: hidden before spreading this object in, and a shorthand
                // here would reset that to auto — which is what let every page drag sideways.
                // Something always overflows eventually: MobileAppFrame and Gutters both lay out
                // as grids whose content column is `1fr`, and a `1fr` track keeps min-width:auto,
                // so one unbreakable string (a URL in a description, a long exhibitor name)
                // widens the track past the screen. Hiding the axis here still permits
                // programmatic scrollIntoView, and nested horizontal scrollers keep working.
                overflowX: "hidden",
                overflowY: scrollDisabled ? "hidden" : "auto",
                paddingTop: nativeNav && !edgeToEdgeTop ? insetTop : undefined,
                paddingBottom: takesScreen
                  ? undefined
                  : nativeNav
                    ? insetBottom
                    : nativeTabbarVisible && (pathname === routes.home.path ? homeTab !== "map" : true)
                      ? tabBarOffset + "px"
                      : undefined,
              }}
              // backgroundImageUrl only ever carries the welcome landing photo (WelcomePage is
              // its sole writer). Under native navigation welcome stays mounted and the signal is
              // shared across views, so without this gate every view's MobileAppFrame paints the
              // photo and it bleeds through wherever the page content does not cover (top bar,
              // bottom safe area). Show it only on the welcome route.
              backgroundImageUrl={pathname === "/" ? backgroundImageUrl : ""}
            >
              {/* Wraps the tab bar as well as the routes: both ends of the chrome scroll away
            together on the home page. */}
              {/* Home, Chat and Team's roots draw their header under the status bar themselves. */}
              <ViewTopInsetProvider enabled={nativeNav && edgeToEdgeTopPaths.includes(pathname)}>
                <HomeScrollAwayProvider
                  enabled={layout === "two-pane" ? screenLayout.leadingPane === "home" : pathname === routes.home.path}
                >
                  <Stack>
                    {SHOW_SCROLL_DEBUG_OVERLAY && nativeNav && <ScrollDebugOverlay scrollRef={localScrollRef} />}
                    <NativeTabbarBridge forceHidden={expandMap} />
                    <DebugChatStatus />
                    <FileCacher />
                    {/* A trailing column's root puts its tab's controls on the vertical bar itself. */}
                    {nativeNav && nativePaneSide === "trailing" && !pathname.startsWith("/panes/") && (
                      <TrailingColumnRailAction />
                    )}
                    {nativeNav ? (
                      // Each native split column holds its own screens, one web view apiece.
                      <NativePaneSide>
                        <Routes>{routeElements}</Routes>
                      </NativePaneSide>
                    ) : (
                      <PaneShell
                        layout={screenLayout}
                        enabled={panesReady}
                        height={paneHeight}
                        routesAt={(pathname) => <Routes location={pathname}>{routeElements}</Routes>}
                      >
                        <Routes>{routeElements}</Routes>
                      </PaneShell>
                    )}

                    {/* Outside the bar: the guard runs on surfaces where the bar is not rendered. */}
                    <DisabledTabGuard />

                    {!expandMap && !nativeTabbarReady && !nativeNav && <NavBar railContainer={railContainer} />}
                  </Stack>
                </HomeScrollAwayProvider>
              </ViewTopInsetProvider>
            </MobileAppFrame>
          </Box>
          {isWidthCapped && (
            <>
              <div onWheel={scrollFromGutter} />
              <div ref={setRailContainer} style={tabRailLeft ? { gridRow: 1, gridColumn: 1 } : undefined} />
            </>
          )}
        </Box>
      </RouteGuardProvider>
    </ScrollContainerContext.Provider>
  );
};

interface GuardedRouteProps {
  auth: boolean;
  to: string;
}

export const GuardedRoute = ({ auth, to }: GuardedRouteProps) => {
  if (auth === true) return <Outlet />;
  return <Navigate replace to={to} />;
};

/**
 * Redirects a legacy `/item/<type>/:_id` deeplink to its canonical `/items/...`
 * path. The query string and location state are forwarded so in-app navigations
 * that still target the old paths (passing `state` or `?noEdit=…`) survive the hop.
 */
const ItemRedirect = ({ to }: { to: string }) => {
  const { _id } = useParams();
  const { search, state } = useLocation();
  return <Navigate to={`${to}/${_id}${search}`} state={state} replace />;
};

/**
 * Redirects a legacy `/itemList/:section_id/:cat_id?` deeplink to its canonical
 * `/sections/...` path, forwarding the query string and location state the way
 * `ItemRedirect` does above (the only caller of the old path always appended
 * `?screen=...&ogCode=...`).
 */
const SectionRedirect = () => {
  const { section_id, cat_id } = useParams();
  const { search, state } = useLocation();
  const to = cat_id
    ? routes.section.categoryItems.build({ sectionId: section_id!, categoryId: cat_id })
    : routes.section.items.build({ sectionId: section_id! });
  return <Navigate to={`${to}${search}`} state={state} replace />;
};
